import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMCategoryProgressCard,
  PMLineChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../../app";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import {
  getCompletedItemsDataAndCategories,
  TIMEFRAME_OPTIONS,
} from "../../../utils/getCompletedItemsByTimeframe";
import { getOverviewCardCopy } from "../get-card-copy";

export function addCategoryLevelAndItemsOverTimeCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const { language, settings, projectName, projectNamePlural, getCardCopy } =
    getOverviewCardCopy(app);
  const resolvedLanguage = language || "en";
  const categoryLabel =
    settings?.configuration?.fieldsDefinition?.find(
      (field) => field.fieldName === "dropdownFieldOne"
    )?.label ?? getLocalizedMessage(resolvedLanguage, "category");

  const completedItemsPerCategoryLevelComponent = view.addComponent({
    component: PMCategoryProgressCard,
    layoutProps: {
      componentId: "completedItemsPerCategoryLevelComponent",
      labels: ["progress"],
      description: getCardCopy(
        resolvedLanguage,
        "completedItemsByCategoryLevel",
        {
          categoryLabel,
        }
      ).description,
      title: getCardCopy(resolvedLanguage, "completedItemsByCategoryLevel", {
        categoryLabel,
      }).title,
    },
  });

  completedItemsPerCategoryLevelComponent.mapLayoutProps(
    ({ language, settings }) => {
      const currentLanguage = language || "en";
      const categoryLabel =
        settings?.configuration?.fieldsDefinition?.find(
          (field) => field.fieldName === "dropdownFieldOne"
        )?.label ?? getLocalizedMessage(currentLanguage, "category");

      return {
        componentId: "completedItemsPerCategoryLevelComponent",
        labels: ["progress"],
        title: getCardCopy(currentLanguage, "completedItemsByCategoryLevel", {
          categoryLabel,
        }).title,
        description: getCardCopy(
          currentLanguage,
          "completedItemsByCategoryLevel",
          {
            categoryLabel,
          }
        ).description,
      };
    }
  );

  completedItemsPerCategoryLevelComponent.mapState(
    ({ dashboard, settings, language }) => {
      const currentLanguage = language || "en";
      const categoryLabel =
        settings?.configuration?.fieldsDefinition?.find(
          (field) => field.fieldName === "dropdownFieldOne"
        )?.label ?? getLocalizedMessage(currentLanguage, "category");
      const title = getLocalizedCardCopy(
        currentLanguage,
        "completedItemsByCategoryLevel",
        {
          itemName: getItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          projectName,
          projectNamePlural,
          categoryLabel,
        }
      ).title;

      return {
        label: "Completed Items Category",
        title,
        icon: "BarChart",
        showInfoIcon: false,
        showMenuIcon: false,
        data:
          dashboard?._calculated
            ?.categoryCountsForCompletedItemsPerCategoryLevel ?? [],
      };
    }
  );

  completedItemsPerCategoryLevelComponent.mapVisibility(
    ({ settings }) =>
      settings?.configuration?.fieldsDefinition
        ?.filter((field) => field.purpose === "itemBasic")
        .find((field) => field.fieldName === "dropdownFieldOne")?.enabled ===
      true
  );

  const completedItemsOverTimeComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "completedItemsOverTimeComponent",
      title: "",
      description: "",
      labels: ["lineChart", "chart", "completedItemsOverTimeComponent"],
    },
  });

  completedItemsOverTimeComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completedItemsOverTimeComponent",
      labels: ["lineChart", "chart", "completedItemsOverTimeComponent"],
      title: getCardCopy(language, "completedItemsOverTime").title,
      description: getCardCopy(language, "completedItemsOverTime").description,
    };
  });

  completedItemsOverTimeComponent.mapState((appState) => {
    const completedItemsOverReportingCycle =
      appState?.dashboard?._calculated?.completedItemsOverReportingCycle;
    const language = appState?.language;
    const { data, categories } = getCompletedItemsDataAndCategories(
      completedItemsOverReportingCycle
    );
    return {
      title: getLocalizedCardCopy(language || "en", "completedItemsOverTime", {
        itemName: getItemName(
          appState.settings?.configuration?.itemName,
          language || "en"
        ),
        itemNamePlural: getPluralOfItemName(
          appState.settings?.configuration?.itemName,
          language || "en"
        ),
        projectName,
        projectNamePlural,
      }).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel:
        getLocalizedMessage(language, "completedItems") || "Completed Items",
    };
  });

  const completedCumulativeItemsOverTimeComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "completedCumulativeItemsOverTimeComponent",
      title: "",
      description: "",
      labels: ["lineChart", "chart", "accumulatedCompletedItemsOverTime"],
    },
  });

  completedCumulativeItemsOverTimeComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completedCumulativeItemsOverTimeComponent",
      labels: ["lineChart", "chart", "accumulatedCompletedItemsOverTime"],
      title: getCardCopy(language, "completedItemsOverTimeCumulative").title,
      description: getCardCopy(language, "completedItemsOverTimeCumulative")
        .description,
    };
  });

  completedCumulativeItemsOverTimeComponent.mapState((appState) => {
    const accumulatedCompletedItemsOverReportingCycle =
      appState?.dashboard?._calculated
        ?.accumulatedCompletedItemsOverReportingCycle;
    const language = appState?.language;
    const { data, categories } = getCompletedItemsDataAndCategories(
      accumulatedCompletedItemsOverReportingCycle
    );

    return {
      title: getLocalizedCardCopy(
        language || "en",
        "completedItemsOverTimeCumulative",
        {
          itemName: getItemName(
            appState.settings?.configuration?.itemName,
            language || "en"
          ),
          itemNamePlural: getPluralOfItemName(
            appState.settings?.configuration?.itemName,
            language || "en"
          ),
          projectName,
          projectNamePlural,
        }
      ).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel:
        getLocalizedMessage(language, "completedItems") || "Completed Items",
    };
  });
}
